import { getSocket } from '@/features/atendimento/socket';
import { ActionIconButton } from '@/shared/components/ui/ActionIconButton';
import { Button } from '@/shared/components/ui/Button';
import { ToggleCard } from '@/shared/components/ui/Switch';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useAuthStore } from '@/shared/stores/auth-store';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, KeyRound, Loader2, Pencil, Plus, Power, Trash2, User, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  type AccessGroupRow,
  type DepartmentRow,
  type UserRow,
  createUser,
  deleteUser,
  listAccessGroups,
  listAccessProfiles,
  listDepartments,
  listUsers,
  resetUserPassword,
  revokeUserSessions,
  updateUser,
} from '../api';
import { type WhatsappNumber, getPlanUsage, listChannels } from '@/features/channels/api';

/**
 * 403 ADMIN_ONLY com a lista velha (alguém virou admin depois que ela carregou):
 * recarrega a lista pra a linha passar a mostrar "Só administrador".
 */
function recarregaSeSoAdmin(err: unknown, qc: QueryClient): void {
  if (err instanceof ApiError && err.code === 'ADMIN_ONLY') {
    void qc.invalidateQueries({ queryKey: ['settings-users'] });
  }
}

export function UsersTab() {
  const qc = useQueryClient();
  const usersQ = useQuery({
    queryKey: ['settings-users'],
    queryFn: () => listUsers(),
    // Presença atualiza AO VIVO via evento `presence.changed` (useEffect abaixo).
    // Este poll de 60s é só rede de segurança (cobre evento perdido em flap de
    // socket). refetchIntervalInBackground fica false (default) → pausa em aba oculta.
    refetchInterval: 60_000,
  });
  const deptsQ = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const groupsQ = useQuery({ queryKey: ['access-groups'], queryFn: listAccessGroups });
  // Todos os canais do tenant — opções pro admin marcar quais o user pode usar
  // pra INICIAR atendimento (vínculo user_whatsapp_numbers).
  const channelsQ = useQuery({ queryKey: ['channels'], queryFn: listChannels });

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [resetting, setResetting] = useState<UserRow | null>(null);

  // Uso vs limite do plano — barra o "Novo usuário" com toast quando o limite
  // já foi atingido (o gate no backend segue como autoridade). Mudança de
  // limite no Super chega via `tenant.flags_changed` (invalida a query).
  const planUsageQuery = useQuery({
    queryKey: ['plan-usage'],
    queryFn: getPlanUsage,
    staleTime: 300_000,
  });

  const all = usersQ.data ?? [];
  const { visible, hasMore, sentinelRef } = useClientPagination(all);
  // Coluna "Grupos de acesso" só aparece se a empresa tem algum grupo.
  const grupos = groupsQ.data ?? [];
  const temGrupos = grupos.length > 0;
  const colunas = temGrupos ? 7 : 6;

  const me = useAuthStore((s) => s.user);
  // Só administrador mexe em administrador (decisão do dono, 29/out/2026): quem
  // gerencia usuários pelo perfil com Configurações não vê as ações nas linhas
  // de admin nem a opção "Admin" no cadastro (a API recusa de qualquer jeito).
  const souAdmin = me?.profile === 'admin';

  const revokeMut = useMutation({
    mutationFn: (u: UserRow) => revokeUserSessions(u.id),
    onSuccess: (_d, u) =>
      toast.success('Usuário deslogado', {
        description: `${u.name} foi desconectado de todos os dispositivos.`,
      }),
    onError: (err) => {
      recarregaSeSoAdmin(err, qc);
      toast.error(err instanceof ApiError ? err.problem.title : 'Falha ao deslogar');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (u: UserRow) => deleteUser(u.id),
    onSuccess: (_d, u) => {
      toast.success('Usuário excluído', { description: `${u.name} foi removido.` });
      void qc.invalidateQueries({ queryKey: ['settings-users'] });
      // A contagem de usuários de cada perfil (aba Perfis de Acesso) muda junto.
      void qc.invalidateQueries({ queryKey: ['access-profiles'] });
    },
    // Erro mais comum: atendimentos abertos (409) — o detail traz "transfira/feche".
    onError: (err) => {
      recarregaSeSoAdmin(err, qc);
      toast.error(err instanceof ApiError ? err.problem.title : 'Falha ao excluir', {
        ...(err instanceof ApiError && err.problem.detail
          ? { description: err.problem.detail }
          : {}),
      });
    },
  });

  // Presença ao vivo: atualiza o pontinho online/offline na hora que alguém
  // conecta/desconecta ou muda a disponibilidade (evento na tenant-room).
  useEffect(() => {
    const socket = getSocket();
    const onPresence = (p: { user_id: number; online: boolean }): void => {
      qc.setQueryData<UserRow[]>(['settings-users'], (prev) =>
        prev?.map((u) => (u.id === p.user_id ? { ...u, online: p.online } : u)),
      );
    };
    socket.on('presence.changed', onPresence);
    return () => {
      socket.off('presence.changed', onPresence);
    };
  }, [qc]);

  return (
    <section className="flex h-full flex-col overflow-hidden">
      <header className="mb-5 flex flex-shrink-0 items-center justify-between">
        <div>
          <h2
            className="font-bold text-[20px] text-n-900"
            style={{ letterSpacing: '-.02em', marginBottom: '4px' }}
          >
            Usuários
          </h2>
          <p className="text-[13px] text-n-500">Gerencie atendentes e admins do tenant.</p>
        </div>
        <Button
          onClick={() => {
            const u = planUsageQuery.data;
            if (u && u.max_users != null && u.users_active >= u.max_users) {
              toast.error(
                `Limite de ${u.max_users} ${u.max_users === 1 ? 'usuário' : 'usuários'} do plano atingido`,
                { description: 'Fale com o suporte para ampliar o plano.' },
              );
              return;
            }
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Novo usuário
        </Button>
      </header>

      {!usersQ.isLoading && all.length > 0 && (
        <p className="mb-2 flex-shrink-0 text-[12px] text-n-500">
          {visible.length === all.length
            ? `${all.length} ${all.length === 1 ? 'usuário' : 'usuários'}`
            : `Mostrando ${visible.length} de ${all.length} usuários`}
        </p>
      )}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Nome
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Email
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Perfil
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Iniciar atendimento
              </th>
              <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Departamentos
              </th>
              {temGrupos && (
                <th className="px-[14px] py-[10px] text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                  Grupos de acesso
                </th>
              )}
              <th className="px-[14px] py-[10px] text-right text-[11px] font-bold uppercase tracking-[.05em] text-n-500 whitespace-nowrap">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {usersQ.isLoading && (
              <tr>
                <td colSpan={colunas} className="px-3 py-8 text-center">
                  <Loader2 size={18} className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!usersQ.isLoading && all.length === 0 && (
              <tr>
                <td colSpan={colunas} className="px-3 py-12 text-center text-n-500">
                  Nenhum usuário cadastrado.
                </td>
              </tr>
            )}
            {visible.map((u) => (
              <tr key={u.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px] font-medium text-n-900">
                  <span className="flex items-center gap-2.5">
                    {/* Avatar (foto ou ícone) com badge de presença sobreposto. */}
                    <span className="relative flex-shrink-0">
                      <span className="relative flex h-[32px] w-[32px] items-center justify-center overflow-hidden rounded-pill bg-n-100 text-n-400">
                        <User size={18} strokeWidth={1.5} />
                        {u.avatar_url && (
                          <img
                            key={u.avatar_url}
                            src={u.avatar_url}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        )}
                      </span>
                      <span
                        className={`-right-0.5 -bottom-0.5 absolute h-3.5 w-3.5 rounded-pill border-2 border-n-0 ${
                          u.online ? 'bg-success-500' : 'bg-n-300'
                        }`}
                        title={u.online ? 'Online' : 'Offline'}
                        aria-label={u.online ? 'Online' : 'Offline'}
                      />
                    </span>
                    {u.name}
                  </span>
                </td>
                <td className="px-[14px] py-[11px] text-n-700">{u.email}</td>
                <td className="px-[14px] py-[11px]">
                  <span className="rounded-pill bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700">
                    {u.profile === 'admin' ? 'Admin' : (u.access_profile_name ?? 'Atendente')}
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex flex-wrap gap-1">
                    {u.whatsapp_number_ids.length === 0 ? (
                      <span className="text-[11px] text-n-400">—</span>
                    ) : (
                      u.whatsapp_number_ids.map((cid) => {
                        const c = channelsQ.data?.find((x) => x.id === cid);
                        return (
                          <span
                            key={cid}
                            className="rounded-pill bg-n-100 px-2 py-[2px] font-semibold text-[10px] text-n-600"
                          >
                            {c?.displayName ?? c?.displayNumber ?? `Canal ${cid}`}
                          </span>
                        );
                      })
                    )}
                  </div>
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex flex-wrap gap-1">
                    {u.departments.map((d) => (
                      <span
                        key={d.id}
                        className="rounded-pill px-2 py-[2px] font-semibold text-[10px]"
                        style={{ background: `${d.color}1f`, color: d.color }}
                      >
                        {d.name}
                      </span>
                    ))}
                  </div>
                </td>
                {temGrupos && (
                  <td className="px-[14px] py-[11px]">
                    <div className="flex flex-wrap gap-1">
                      {u.access_group_ids.length === 0 ? (
                        <span className="text-[11px] text-n-400">—</span>
                      ) : (
                        u.access_group_ids.map((gid) => (
                          <span
                            key={gid}
                            className="rounded-pill bg-n-100 px-2 py-[2px] font-semibold text-[10px] text-n-600"
                          >
                            {grupos.find((g) => g.id === gid)?.name ?? `Grupo ${gid}`}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                )}
                <td className="px-[14px] py-[11px]">
                  {/* Ordem: Trocar senha · Invalidar sessões (deslogar) · Editar · Excluir */}
                  {!souAdmin && u.profile === 'admin' ? (
                    <div className="text-right text-[11px] text-n-400">Só administrador</div>
                  ) : (
                    <div className="flex justify-end gap-1">
                      <ActionIconButton
                        icon={KeyRound}
                        label="Trocar senha"
                        onClick={() => setResetting(u)}
                      />
                      {/* Invalidar sessões = deslogar de todos os dispositivos.
                        Quem gerencia usuários (admin ou perfil com Configurações),
                        nunca em si mesmo; aparece SEMPRE (online ou não). */}
                      {me != null && me.id !== u.id && (
                        <ActionIconButton
                          icon={Power}
                          label="Invalidar sessões (deslogar de todos os dispositivos)"
                          tone="danger"
                          disabled={revokeMut.isPending}
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Invalidar sessões de ${u.name}?`,
                              description:
                                'O usuário será desconectado de todos os dispositivos e precisará entrar de novo.',
                              confirmText: 'Invalidar sessões',
                              variant: 'danger',
                            });
                            if (ok) revokeMut.mutate(u);
                          }}
                        />
                      )}
                      <ActionIconButton
                        icon={Pencil}
                        label="Editar"
                        tone="brand"
                        onClick={() => setEditing(u)}
                      />
                      {/* Excluir (soft delete): some da lista + libera o email,
                        preserva histórico. Quem gerencia usuários, nunca em si mesmo. */}
                      {me != null && me.id !== u.id && (
                        <ActionIconButton
                          icon={Trash2}
                          label="Excluir usuário"
                          tone="danger"
                          disabled={deleteMut.isPending}
                          onClick={async () => {
                            const ok = await confirm({
                              title: `Excluir ${u.name}?`,
                              description:
                                'O usuário some da lista e o email fica livre pra reuso. O histórico de atendimentos é preservado. Não é possível se houver atendimentos abertos.',
                              confirmText: 'Excluir',
                              variant: 'danger',
                            });
                            if (ok) deleteMut.mutate(u);
                          }}
                        />
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={6} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <UserFormModal
          user={editing}
          departments={deptsQ.data ?? []}
          accessGroups={groupsQ.data ?? []}
          podeDarAdmin={souAdmin}
          // "Iniciar Atendimento" lista só canais que PODEM iniciar conversa: o
          // Instagram não deixa (só responde quem escreveu antes) — a lista de
          // canais do atendimento ativo (/me) já o tira.
          channels={(channelsQ.data ?? []).filter((c) => c.channelType !== 'instagram')}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {resetting && <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} />}
    </section>
  );
}

function UserFormModal({
  user,
  departments,
  accessGroups,
  podeDarAdmin,
  channels,
  onClose,
}: {
  user: UserRow | null;
  departments: DepartmentRow[];
  accessGroups: AccessGroupRow[];
  /** Só admin vê a opção "Admin" (quem gerencia pelo perfil não promove). */
  podeDarAdmin: boolean;
  channels: WhatsappNumber[];
  onClose: () => void;
}) {
  // Com o aviso de rebaixamento aberto, o Esc é só dele — sem isso o mesmo Esc
  // fechava também o formulário e perdia o que foi digitado.
  const [confirmando, setConfirmando] = useState(false);
  useEscapeClose(!confirmando, onClose);
  const qc = useQueryClient();
  const editing = user !== null;
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  // Perfil numa escolha só: 'admin' | 'attendant' | 'perfil:<id>' (perfil de
  // acesso = atendente + telas do perfil). Vide perfil-de-acesso.ts.
  const profilesQ = useQuery({ queryKey: ['access-profiles'], queryFn: listAccessProfiles });
  const [perfilSel, setPerfilSel] = useState<string>(
    user?.profile === 'admin'
      ? 'admin'
      : user?.access_profile_id
        ? `perfil:${user.access_profile_id}`
        : 'attendant',
  );
  // O papel só vai no PATCH quando o campo Perfil mudou: com a lista velha (alguém
  // mudou o papel dessa pessoa depois que ela carregou), reenviar o valor antigo
  // rebaixava sem o aviso abaixo — e rebaixar admin desconecta na hora.
  const [perfilInicial] = useState(perfilSel);
  const perfilMudou = perfilSel !== perfilInicial;
  const profile: 'admin' | 'attendant' = perfilSel === 'admin' ? 'admin' : 'attendant';
  const accessProfileId = perfilSel.startsWith('perfil:')
    ? Number(perfilSel.slice('perfil:'.length))
    : null;
  const [deptIds, setDeptIds] = useState<number[]>(user?.departments.map((d) => d.id) ?? []);
  const [groupIds, setGroupIds] = useState<number[]>(user?.access_group_ids ?? []);
  // Canais que o user pode usar pra INICIAR atendimento (vínculo user_whatsapp_numbers).
  const [channelIds, setChannelIds] = useState<number[]>(user?.whatsapp_number_ids ?? []);
  // Gestor: permite o atendente desativar a própria assinatura. Default true.
  const [allowDisableSignature, setAllowDisableSignature] = useState(
    user?.allow_disable_signature ?? true,
  );
  // Chaves do usuário (seção "Permissões e atendimento").
  const [verTodos, setVerTodos] = useState(user?.view_all_tickets ?? false);
  const [naoRecebe, setNaoRecebe] = useState(user?.skip_auto_assign ?? false);
  // Limite de atendimentos ativos ao mesmo tempo. 0 = sem limite (padrão).
  const [limite, setLimite] = useState(String(user?.max_active_tickets ?? 0));
  const limiteNum = Math.min(999, Math.max(0, Number.parseInt(limite, 10) || 0));
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (editing && user) {
        return updateUser(user.id, {
          name,
          email,
          ...(perfilMudou && { profile, access_profile_id: accessProfileId }),
          department_ids: deptIds,
          access_group_ids: groupIds,
          whatsapp_number_ids: channelIds,
          allow_disable_signature: allowDisableSignature,
          max_active_tickets: limiteNum,
          view_all_tickets: verTodos,
          skip_auto_assign: naoRecebe,
        });
      }
      return createUser({
        name,
        email,
        password,
        profile,
        access_profile_id: accessProfileId,
        department_ids: deptIds,
        access_group_ids: groupIds,
        whatsapp_number_ids: channelIds,
        allow_disable_signature: allowDisableSignature,
        max_active_tickets: limiteNum,
        view_all_tickets: verTodos,
        skip_auto_assign: naoRecebe,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings-users'] });
      // A contagem de usuários de cada perfil (aba Perfis de Acesso) muda junto.
      void qc.invalidateQueries({ queryKey: ['access-profiles'] });
      onClose();
    },
    onError: (err) => {
      // Limite do plano: toast padrão (o pré-check no botão cobre o caso
      // comum; aqui é o backstop quando o uso mudou entre o clique e o save).
      if (err instanceof ApiError && err.code === 'USER_LIMIT_REACHED') {
        toast.error(err.problem.title, {
          description: 'Fale com o suporte para ampliar o plano.',
        });
      }
      recarregaSeSoAdmin(err, qc);
      setError(err instanceof ApiError ? err.problem.title : 'Falha ao salvar');
    },
  });

  // Tirar o papel de admin desconecta a pessoa na hora (decisão do dono,
  // 29/out/2026): confirma antes, com o aviso certo quando é você mesmo.
  const meuId = useAuthStore((s) => s.user?.id);
  const salvar = async (): Promise<void> => {
    if (editing && user && perfilMudou && perfilInicial === 'admin' && profile !== 'admin') {
      const souEu = meuId === user.id;
      // O Esc volta ao formulário só DEPOIS que a tecla que fechou o aviso
      // terminou de propagar (setTimeout): devolvido na hora, o listener voltava
      // ao document no meio do mesmo evento e esse Esc fechava o formulário também.
      setConfirmando(true);
      const ok = await confirm({
        title: souEu
          ? 'Deixar de ser administrador?'
          : `Tirar o acesso de administrador de ${user.name}?`,
        description: souEu
          ? 'Você perde o acesso de administrador e sai do sistema na hora. Ao entrar de novo, já entra com o novo perfil.'
          : `${user.name} perde o acesso de administrador e sai do sistema na hora. Ao entrar de novo, já entra com o novo perfil.`,
        confirmText: souEu ? 'Deixar de ser administrador' : 'Tirar acesso de administrador',
        variant: 'danger',
      }).finally(() => setTimeout(() => setConfirmando(false), 0));
      if (!ok) return;
    }
    mut.mutate();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* 2 campos por linha, chaves uma embaixo da outra (dono, 27/out/2026);
          rola por dentro quando a tela é baixa. */}
      <div className="flex max-h-[90vh] w-[600px] max-w-[95vw] flex-col overflow-hidden rounded-lg bg-n-0 shadow-lg">
        <div className="flex flex-none items-center justify-between px-6 pt-6">
          <h3 className="font-bold text-[16px]">{editing ? 'Editar usuário' : 'Novo usuário'}</h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 grid min-h-0 flex-1 grid-cols-1 content-start gap-3 overflow-y-auto px-6 pb-1 sm:grid-cols-2">
          <Field label="Nome" value={name} onChange={setName} />
          <Field label="Email" type="email" value={email} onChange={setEmail} />
          {!editing && (
            <PasswordField
              label="Senha temporária"
              value={password}
              onChange={setPassword}
              hint="Mínimo 8 caracteres."
            />
          )}
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Perfil</span>
            <select
              value={perfilSel}
              onChange={(e) => setPerfilSel(e.target.value)}
              className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px]"
            >
              <option value="attendant">Atendente</option>
              {podeDarAdmin && <option value="admin">Admin</option>}
              {(profilesQ.data ?? []).length > 0 && (
                <optgroup label="Perfis de acesso">
                  {(profilesQ.data ?? []).map((p) => (
                    <option key={p.id} value={`perfil:${p.id}`}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Permitir desativar assinatura</span>
            <select
              value={allowDisableSignature ? 'sim' : 'nao'}
              onChange={(e) => setAllowDisableSignature(e.target.value === 'sim')}
              className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px]"
            >
              <option value="sim">Sim</option>
              <option value="nao">Não</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Limite de atendimentos</span>
            {/* Dica no hover do campo pelo `title` (TooltipLayer desenha o
                balão, sem quebra de linha e com largura máxima — texto curto).
                aria-label explícito: sem ele o TooltipLayer copia a dica pra lá
                e o campo perde o nome "Limite de atendimentos". */}
            <input
              title="Ativos ao mesmo tempo. 0 = sem limite."
              aria-label="Limite de atendimentos"
              type="number"
              min={0}
              max={999}
              step={1}
              value={limite}
              onChange={(e) => setLimite(e.target.value)}
              onBlur={() => setLimite(String(limiteNum))}
              className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] outline-none focus:border-brand-500"
            />
          </label>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <span className="font-semibold text-[12px] text-n-700">Permissões e atendimento</span>
            <div className="flex flex-col gap-2">
              <ToggleCard
                labelSize="sm"
                label="Não receber tickets via distribuição automática"
                checked={naoRecebe}
                onChange={setNaoRecebe}
              />
              {/* Admin já vê todos: chave ligada e travada. */}
              <ToggleCard
                labelSize="sm"
                label="Visualizar tickets de outros usuários dos seus departamentos"
                checked={profile === 'admin' || verTodos}
                onChange={setVerTodos}
                disabled={profile === 'admin'}
              />
            </div>
          </div>
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="font-semibold text-[12px] text-n-700">Departamentos</span>
            <div className="flex flex-wrap gap-1.5">
              {departments.length === 0 && (
                <span className="text-[12px] text-n-500">Cadastre departamentos primeiro.</span>
              )}
              {departments.map((d) => {
                const active = deptIds.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() =>
                      setDeptIds((prev) =>
                        active ? prev.filter((id) => id !== d.id) : [...prev, d.id],
                      )
                    }
                    className={`rounded-pill border px-2.5 py-1 text-[11px] font-semibold transition ${
                      active ? 'border-transparent text-white' : 'border-n-200 bg-n-0 text-n-700'
                    }`}
                    style={active ? { background: d.color } : undefined}
                  >
                    {d.name}
                  </button>
                );
              })}
            </div>
          </label>
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="font-semibold text-[12px] text-n-700">Grupos de acesso</span>
            <div className="flex flex-wrap gap-1.5">
              {accessGroups.length === 0 && (
                <span className="text-[12px] text-n-500">
                  Nenhum grupo cadastrado. Crie em “Grupos de Acesso”.
                </span>
              )}
              {accessGroups.map((g) => {
                const active = groupIds.includes(g.id);
                return (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() =>
                      setGroupIds((prev) =>
                        active ? prev.filter((id) => id !== g.id) : [...prev, g.id],
                      )
                    }
                    className={`rounded-pill border px-2.5 py-1 text-[11px] font-semibold transition ${
                      active
                        ? 'border-transparent bg-brand-500 text-white'
                        : 'border-n-200 bg-n-0 text-n-700 hover:bg-n-50'
                    }`}
                  >
                    {g.name}
                  </button>
                );
              })}
            </div>
          </label>
          <label className="flex flex-col gap-1 sm:col-span-2">
            <span className="font-semibold text-[12px] text-n-700">Iniciar Atendimento</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {channels.length === 0 && (
                <span className="text-[12px] text-n-500">Nenhum canal cadastrado.</span>
              )}
              {channels.map((c) => {
                const active = channelIds.includes(c.id);
                const label = c.displayName ?? c.displayNumber ?? `Canal ${c.id}`;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() =>
                      setChannelIds((prev) =>
                        active ? prev.filter((id) => id !== c.id) : [...prev, c.id],
                      )
                    }
                    className={`rounded-pill border px-2.5 py-1 text-[11px] font-semibold transition ${
                      active
                        ? 'border-transparent bg-brand-500 text-white'
                        : 'border-n-200 bg-n-0 text-n-700 hover:bg-n-50'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </label>
        </div>
        {error && (
          <div className="mx-6 mt-3 flex-none rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}
        <div className="flex flex-none justify-end gap-2 px-6 pt-4 pb-6">
          <Button variant="ghost" onClick={onClose} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => void salvar()} disabled={mut.isPending}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }: { user: UserRow; onClose: () => void }) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => resetUserPassword(user.id, password),
    onSuccess: () => {
      toast.success('Senha redefinida', {
        description: `A senha de ${user.name} foi alterada. As sessões ativas dele foram encerradas.`,
      });
      onClose();
    },
    onError: (err) => {
      recarregaSeSoAdmin(err, qc);
      setError(err instanceof ApiError ? err.problem.title : 'Falha');
    },
  });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[400px] rounded-lg bg-n-0 p-6 shadow-lg">
        <h3 className="font-bold text-[16px]">Resetar senha de {user.name}</h3>
        <p className="mt-2 text-[12px] text-n-500">
          Defina a nova senha. As sessões ativas do usuário serão encerradas.
        </p>
        <div className="mt-3">
          <PasswordField
            label="Nova senha"
            value={password}
            onChange={setPassword}
            hint="Mínimo 8 caracteres."
          />
        </div>
        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={() => mut.mutate()} disabled={mut.isPending || password.length < 8}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Resetar
          </Button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-semibold text-[12px] text-n-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] outline-none focus:border-brand-500"
      />
    </label>
  );
}

/**
 * Campo de senha com botão "olhinho" (mostrar/ocultar) e dica opcional.
 * Usado nos fluxos de criar usuário e resetar senha.
 */
function PasswordField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <label className="flex flex-col gap-1">
      <span className="font-semibold text-[12px] text-n-700">{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 pr-10 text-[13px] outline-none focus:border-brand-500"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          title={show ? 'Ocultar senha' : 'Mostrar senha'}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-n-500 transition-colors hover:text-n-700"
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {hint && <span className="text-[11px] text-n-500">{hint}</span>}
    </label>
  );
}
